import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listingService } from '../services';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// TEMPORARY: Set test user authentication data if not already set
// Using the real donor ID from the database - donor_01HPCQ / Local Restaurant
const TEMP_TEST_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJkb25vcl8wMUhQQ1EiLCJlbWFpbCI6InJlc3RhdXJhbnRAZXhhbXBsZS5jb20iLCJyb2xlIjoiZG9ub3IiLCJuYW1lIjoiTG9jYWwgUmVzdGF1cmFudCIsImlhdCI6MTcxNzkxNDQ0Nn0.beXDj083iJ3CrCMpMXk3wReRnD6YUwpIZ0ashcvK9Jc';
const TEMP_TEST_USER = {
  id: 'donor_01HPCQ',
  name: 'Local Restaurant',
  role: 'donor',
  email: 'restaurant@example.com'
};

// Set test auth data if not already present
if (!localStorage.getItem('wastenet_token')) {
  console.log('Setting temporary test auth token for development');
  localStorage.setItem('wastenet_token', TEMP_TEST_TOKEN);
  localStorage.setItem('wastenet_user', JSON.stringify(TEMP_TEST_USER));
}

function ManageListingsPage() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Since the NotificationContext doesn't provide a showNotification function,
  // let's implement our own local notification system
  const [notification, setNotification] = useState({ message: '', type: '' });
  
  // Using useCallback to prevent the function from being recreated on each render
  const showNotification = useCallback((message, type) => {
    setNotification({ message, type });
    // Auto-clear notification after 3 seconds
    setTimeout(() => {
      setNotification({ message: '', type: '' });
    }, 3000);
  }, []);

  // Redirect if not donor
  useEffect(() => {
    if (user && user.role !== 'donor') {
      showNotification('Only donors can manage listings', 'error');
      navigate('/dashboard');
    }
  }, [user, navigate, showNotification]);

  // Fetch listings when component mounts
  useEffect(() => {
    fetchListings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchListings = async () => {
    setLoading(true);
    try {
      // Check if we have auth token and user in localStorage
      const token = localStorage.getItem('wastenet_token');
      const userStr = localStorage.getItem('wastenet_user');
      
      console.log('%c[ManageListings] Auth check', 'background: #3498db; color: white; padding: 2px 4px; border-radius: 3px;');
      console.log('Auth token exists:', !!token);
      if (token) {
        console.log('Token first 20 chars:', token.substring(0, 20) + '...');
      }
      console.log('User data exists:', !!userStr);
      
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          console.log('User ID:', userData.id);
          console.log('User role:', userData.role);
          console.log('Full user data:', userData);
        } catch (e) {
          console.error('Failed to parse user data:', e);
        }
      }
      
      console.log('%c[ManageListings] Starting API calls', 'background: #3498db; color: white; padding: 2px 4px; border-radius: 3px;');
      
      // First try making a direct API call with the authorization header explicitly set
      try {
        console.log('Making direct API call with explicit auth header...');
        const directResponse = await fetch('http://localhost:8787/api/listings/donor/me', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (directResponse.ok) {
          const data = await directResponse.json();
          console.log('Direct fetch response:', data);
        } else {
          console.error('Direct fetch failed:', await directResponse.text());
        }
      } catch (e) {
        console.error('Direct fetch error:', e);
      }
      
      // Try the direct manage endpoint
      try {
        console.log('Calling manage endpoint via axios...');
        const manageResponse = await api.get('/listings/manage');
        console.log('Direct manage endpoint response:', manageResponse.data);
        
        if (manageResponse.data && manageResponse.data.listings && manageResponse.data.listings.length > 0) {
          setListings(manageResponse.data.listings);
          return; // Exit early if we got data
        }
      } catch (e) {
        console.error('Manage endpoint failed:', e.message, e.response?.data);
      }
      
      // Then try the normal getMyListings service
      console.log('Trying getMyListings service...');
      try {
        const response = await listingService.getMyListings();
        console.log('Donor listings response from service:', response);
        
        // Extract listings based on the response structure
        let listingsData = [];
        
        if (response?.listings?.results && Array.isArray(response.listings.results)) {
          listingsData = response.listings.results;
        } else if (Array.isArray(response?.listings)) {
          listingsData = response.listings;
        } else if (Array.isArray(response)) {
          listingsData = response;
        } else {
          console.error('Could not find listings array in response:', response);
        }
        
        console.log('Final listings data:', listingsData);
        setListings(listingsData);
      } catch (err) {
        console.error('getMyListings service call failed:', err.message, err.response?.data);
        throw err; // Re-throw to be caught by outer try/catch
      }
    } catch (err) {
      console.error('%c[ManageListings] Failed to fetch listings:', 'background: #e74c3c; color: white; padding: 2px 4px; border-radius: 3px;', err);
      setError('Could not load your listings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this listing?')) {
      try {
        await listingService.deleteListing(id);
        showNotification('Listing deleted successfully', 'success');
        fetchListings(); // Refresh the list
      } catch (err) {
        console.error('Failed to delete listing:', err);
        showNotification('Failed to delete listing', 'error');
      }
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date)) {
        return dateString;
      }
      return date.toLocaleDateString();
    } catch (err) {
      return dateString || 'N/A';
    }
  };

  const getStatusClass = (expiryDate) => {
    try {
      const expiry = new Date(expiryDate);
      const now = new Date();
      
      if (expiry < now) {
        return 'text-red-800 bg-red-100'; // Expired
      }
      
      const diffTime = expiry - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 1) {
        return 'text-orange-800 bg-orange-100'; // Expires today/tomorrow
      }
      
      return 'text-green-800 bg-green-100'; // Active
    } catch (err) {
      return 'text-gray-800 bg-gray-100'; // Default
    }
  };

  const getListingImage = (listing) => {
    try {
      if (!listing.images) return '/placeholder-food.jpg';
      
      // If it's a string, try to parse it as JSON first
      if (typeof listing.images === 'string') {
        try {
          const parsedImages = JSON.parse(listing.images);
          return Array.isArray(parsedImages) && parsedImages.length > 0
            ? parsedImages[0]
            : '/placeholder-food.jpg';
        } catch (e) {
          return listing.images || '/placeholder-food.jpg';
        }
      }
      
      // If it's already an array
      if (Array.isArray(listing.images)) {
        return listing.images.length > 0
          ? listing.images[0]
          : '/placeholder-food.jpg';
      }
      
      return '/placeholder-food.jpg';
    } catch (e) {
      console.error('Error getting listing image:', e);
      return '/placeholder-food.jpg';
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6">Manage Your Listings</h1>
      
      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
      
      {notification.message && (
        <div className={`px-4 py-3 rounded mb-4 ${notification.type === 'success' ? 'bg-green-100 border border-green-400 text-green-700' : 'bg-red-100 border border-red-400 text-red-700'}`}>
          {notification.message}
        </div>
      )}
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-gray-600">View, edit and manage all your food donation listings</p>
        </div>
        <Link
          to="/create-listing"
          className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded inline-flex items-center"
        >
          <span className="mr-2">+</span>
          Add New Listing
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
          <p>{error}</p>
        </div>
      ) : listings.length === 0 ? (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-md shadow-sm">
          <div className="flex items-center">
            <div className="text-yellow-500 mr-4">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="font-bold">No listings found</p>
              <p className="text-sm">You haven't created any food donation listings yet.</p>
            </div>
          </div>
          <Link
            to="/create-listing"
            className="mt-4 inline-block bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded"
          >
            Create Your First Listing
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Listing
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiry Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {listings.map((listing) => (
                <tr key={listing.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        <img
                          className="h-12 w-12 rounded-md object-cover"
                          src={getListingImage(listing)}
                          alt={listing.title}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/placeholder-food.jpg';
                          }}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{listing.title}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">{listing.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {listing.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{listing.quantity} {listing.quantity_unit}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(listing.expiry_date)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(listing.expiry_date)}`}>
                      {new Date(listing.expiry_date) < new Date() ? 'Expired' : 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Link to={`/listings/${listing.id}`} className="text-primary-600 hover:text-primary-900 mr-4">
                      View
                    </Link>
                    <Link to={`/listings/edit/${listing.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(listing.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ManageListingsPage;
